import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type { ToofiPreferences } from "@/types/student-toofi";

const hasMissingColumnError = (error: { code?: string | null; message?: string | null; details?: string | null; hint?: string | null } | null | undefined, column: string) => {
  if (!error) return false;
  const haystack = `${error.code ?? ""} ${error.message ?? ""} ${error.details ?? ""} ${error.hint ?? ""}`.toLowerCase();
  return haystack.includes(column.toLowerCase()) && (haystack.includes("column") || haystack.includes("schema cache") || haystack.includes("pgrst"));
};

const DEFAULT_PREFERENCES: ToofiPreferences = {
  pushEnabled: true,
  inAppEnabled: true,
  pausedUntil: null,
  quietStart: null,
  quietEnd: null,
  intensity: "standard",
  dailyNudgeCap: 2,
  preferredStudyWindows: [],
};

const normalizeStudyWindows = (value: Json | null | undefined): ToofiPreferences["preferredStudyWindows"] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
      const record = entry as Record<string, unknown>;
      const days = Array.isArray(record.days)
        ? record.days.filter((day): day is number => typeof day === "number" && day >= 0 && day <= 6)
        : [];
      const start = typeof record.start === "string" ? record.start : null;
      const end = typeof record.end === "string" ? record.end : null;
      if (!start || !end) return null;
      return { days, start, end };
    })
    .filter((entry): entry is ToofiPreferences["preferredStudyWindows"][number] => Boolean(entry));
};

const normalizePreferences = (row: Record<string, unknown> | null | undefined): ToofiPreferences => {
  if (!row) return DEFAULT_PREFERENCES;
  const intensity =
    row.intensity === "gentle" || row.intensity === "coach" || row.intensity === "standard"
      ? row.intensity
      : "standard";
  return {
    pushEnabled: typeof row.push_enabled === "boolean" ? row.push_enabled : DEFAULT_PREFERENCES.pushEnabled,
    inAppEnabled: typeof row.in_app_enabled === "boolean" ? row.in_app_enabled : DEFAULT_PREFERENCES.inAppEnabled,
    pausedUntil: typeof row.paused_until === "string" ? row.paused_until : null,
    quietStart: typeof row.quiet_start === "string" ? row.quiet_start : null,
    quietEnd: typeof row.quiet_end === "string" ? row.quiet_end : null,
    intensity,
    dailyNudgeCap:
      typeof row.daily_nudge_cap === "number" ? Math.max(0, Math.min(5, row.daily_nudge_cap)) : DEFAULT_PREFERENCES.dailyNudgeCap,
    preferredStudyWindows: normalizeStudyWindows((row.preferred_study_windows as Json | null | undefined) ?? []),
  };
};

const toRowPayload = (userId: string, preferences: ToofiPreferences) => ({
  user_id: userId,
  push_enabled: preferences.pushEnabled,
  in_app_enabled: preferences.inAppEnabled,
  paused_until: preferences.pausedUntil ?? null,
  quiet_start: preferences.quietStart,
  quiet_end: preferences.quietEnd,
  intensity: preferences.intensity,
  daily_nudge_cap: preferences.dailyNudgeCap,
  preferred_study_windows: preferences.preferredStudyWindows as unknown as Json,
});

export function useToofiPreferences(userId?: string | null) {
  const queryClient = useQueryClient();
  const queryKey = ["toofi-preferences", userId];

  const query = useQuery({
    queryKey,
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return DEFAULT_PREFERENCES;
      const { data, error } = await supabase
        .from("user_notification_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) {
        if (!hasMissingColumnError(error, "paused_until")) throw error;
        const fallback = await supabase
          .from("user_notification_preferences")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle();
        if (fallback.error) throw fallback.error;
        return normalizePreferences((fallback.data as Record<string, unknown> | null) ?? null);
      }
      return normalizePreferences((data as Record<string, unknown> | null) ?? null);
    },
  });

  const mutation = useMutation({
    mutationFn: async (preferences: ToofiPreferences) => {
      if (!userId) throw new Error("Missing user");
      const payload = toRowPayload(userId, preferences);
      const { error } = await supabase.from("user_notification_preferences").upsert(payload as never, { onConflict: "user_id" });
      if (error) {
        if (!hasMissingColumnError(error, "paused_until")) throw error;
        const { paused_until: _pausedUntil, ...legacyPayload } = payload;
        const legacyResult = await supabase.from("user_notification_preferences").upsert(legacyPayload as never, { onConflict: "user_id" });
        if (legacyResult.error) throw legacyResult.error;
        return { ...preferences, pausedUntil: null };
      }
      return preferences;
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKey, preferences);
    },
  });

  const savePreferences = useCallback(
    async (preferences: ToofiPreferences) => mutation.mutateAsync(preferences),
    [mutation],
  );

  return {
    preferences: query.data ?? DEFAULT_PREFERENCES,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    savePreferences,
    refetch: query.refetch,
  };
}
